import { derivePins, deriveRouteStarts } from '@core/utils/plan-edit/pins';

import { PinnedStop, PlanEditSessionState } from './plan-edit.state';

/** Per tab: a duplicated tab carries a copy, and the second to resume takes the session over. */
export const PLAN_EDIT_RELOAD_KEY = 'gfe.planEdit.reload';
/**
 * DL parks a session for 60 s; past that a resume can only fail. The margin lets a slow reload
 * still try, and keeps a stash a tab left days ago from telling a loss nobody is waiting for.
 */
export const PLAN_EDIT_RELOAD_MAX_AGE_MS = 10 * 60_000;

/** What a reloaded tab needs to resume: DL gives back neither the pins nor the unsaved count. */
export interface PlanEditReloadSnapshot {
  planId: string;
  sessionId: string;
  /** When the page went away, in epoch ms. */
  leftAt: number;
  unsavedCount: number;
  pins: Record<string, PinnedStop>;
  routeStarts: Record<string, string>;
  openedRouteUids: string[];
  removedRouteUids: string[];
  routesOpenedAtSave: string[];
}

/**
 * Not `saving`: the save may land while the page reloads, and DL then resumes on the saved plan,
 * a base the reloaded page may never have read, with the unsaved count it stashed gone stale.
 */
const RESUMABLE_MODES: readonly PlanEditSessionState['mode'][] = [
  'editing',
  'disconnected'
];

export function reloadSnapshotOf(
  session: PlanEditSessionState,
  now: number
): PlanEditReloadSnapshot | null {
  const { planId, sessionId } = session;
  if (!RESUMABLE_MODES.includes(session.mode) || !session.resumable) return null;
  if (planId === null || sessionId === null) return null;
  return {
    planId,
    sessionId,
    leftAt: now,
    unsavedCount: session.unsavedCount,
    pins: derivePins(session.pinsAtSave, session.applied, session.cursor),
    routeStarts: deriveRouteStarts(
      session.routeStartsAtSave,
      session.applied,
      session.cursor
    ),
    openedRouteUids: session.openedRouteUids,
    removedRouteUids: session.removedRouteUids,
    routesOpenedAtSave: session.routesOpenedAtSave
  };
}

export function stashReloadSnapshot(
  storage: Storage | null,
  snapshot: PlanEditReloadSnapshot
): void {
  try {
    storage?.setItem(PLAN_EDIT_RELOAD_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage refused (private window, quota): the reload simply will not resume.
  }
}

export function dropReloadSnapshot(storage: Storage | null): void {
  try {
    storage?.removeItem(PLAN_EDIT_RELOAD_KEY);
  } catch {
    // Storage refused: there is nothing it could hand back either.
  }
}

/** Removes the stash whatever it holds; returns it only for `planId` and younger than the max age. */
export function takeReloadSnapshot(
  storage: Storage | null,
  planId: string,
  now: number
): PlanEditReloadSnapshot | null {
  let raw: string | null;
  try {
    raw = storage?.getItem(PLAN_EDIT_RELOAD_KEY) ?? null;
    storage?.removeItem(PLAN_EDIT_RELOAD_KEY);
  } catch {
    return null;
  }
  const snapshot = parse(raw);
  if (!snapshot || snapshot.planId !== planId) return null;
  return now - snapshot.leftAt <= PLAN_EDIT_RELOAD_MAX_AGE_MS ? snapshot : null;
}

function parse(raw: string | null): PlanEditReloadSnapshot | null {
  if (raw === null) return null;
  try {
    const value = JSON.parse(raw) as Partial<PlanEditReloadSnapshot>;
    return typeof value.planId === 'string' &&
      typeof value.sessionId === 'string' &&
      typeof value.leftAt === 'number'
      ? (value as PlanEditReloadSnapshot)
      : null;
  } catch {
    return null;
  }
}
